import { useEffect, useState } from 'react'

import { RefreshCcw, Send } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { DelayRender } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { SelectInput } from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { Form, FormField } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { INSTRUMENTS } from '@/constants/links'
import { FormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/FormValues'
import {
  INSTRUMENT_TYPE_OPTIONS,
  InstrumentType,
} from '@/pages/instruments/new-instrument/constants/instruments'
import { defaultValues } from '@/pages/instruments/new-instrument/default-values'
import { AswapFormFields } from '@/pages/instruments/new-instrument/fields/aswap.fields'
import { AutocallConsolationFormFields } from '@/pages/instruments/new-instrument/fields/autocall-consolation.fields'
import { AutocallStandardFormFields } from '@/pages/instruments/new-instrument/fields/autocall-standard.fields'
import { BondFormFields } from '@/pages/instruments/new-instrument/fields/bond.fields'
import { DigitalFormFields } from '@/pages/instruments/new-instrument/fields/digital.fields'
import { DiverOtcFormFields } from '@/pages/instruments/new-instrument/fields/diver-otc.fields'
import { EquityOtcFormFields } from '@/pages/instruments/new-instrument/fields/equity-otc.fields'
import { EquityFormFields } from '@/pages/instruments/new-instrument/fields/equity.fields'
import { FundFormFields } from '@/pages/instruments/new-instrument/fields/fund.fields'
import { FxFwdFields } from '@/pages/instruments/new-instrument/fields/fxfwd.fields'
import { IndexFormFields } from '@/pages/instruments/new-instrument/fields/index.fields'
import { OtherFormFields } from '@/pages/instruments/new-instrument/fields/other.fields'
import { PhoenixNonMemoryFormFields } from '@/pages/instruments/new-instrument/fields/phoenix-non-memory.fields'
import { RangeAccrualFormFields } from '@/pages/instruments/new-instrument/fields/range-accrual.fields'
import { SupertrackerFormFields } from '@/pages/instruments/new-instrument/fields/supertracker.fields'
import { VanillaDigitalFormFields } from '@/pages/instruments/new-instrument/fields/vanilla-digital.fields'
import { VanillaSpreadFormFields } from '@/pages/instruments/new-instrument/fields/vanilla-spread.fields'
import { VanillaStructuredFormFields } from '@/pages/instruments/new-instrument/fields/vanilla-structured.fields'
import { VanillaFormFields } from '@/pages/instruments/new-instrument/fields/vanilla.fields'
import { ScheduleTable } from '@/pages/instruments/new-instrument/schedule-builders/ScheduleTable'
import { getScheduleLegsFromFormValues } from '@/pages/instruments/new-instrument/schedule-builders/get-schedule'
import { ScheduleLeg } from '@/pages/instruments/new-instrument/schedule-builders/types'
import {
  AllInstrumentsSchemaType,
  allInstrumentsSchema,
} from '@/pages/instruments/new-instrument/schemas'
import { DATABASES, Database } from '@/pages/instruments/new-instrument/schemas/shared/base.schema'
import { useCreateInstrumentMutation } from '@/react-query/mutations/instruments'

import CreateInstrumentIllustration from '/images/create-instrument.png'

export interface NewInstrumentPageProps {}

export const NewInstrumentPage: React.FC<NewInstrumentPageProps> = ({}) => {
  const form = useFormWithSchema({ mode: 'all' }, allInstrumentsSchema)
  const {
    reset,
    formState: { isValid, isSubmitting },
  } = form

  const [instrument_type] = form.watch(['instrument_type'])

  const { Dialog, withConfirmation } = useConfirmModal({
    title: 'Overwrite changes?',
    description:
      'You have manually edited the schedule, this will regenerate the schedule using values inside the form, are you sure you want to do this?',
  })

  const hasSchedule = (
    [
      'autocall-standard',
      'autocall-consolation',
      'autocall-phoenix',
      'digital',
      'vanilla-spread',
      'vanilla-structured',
      'range-accrual',
      'supertracker',
      'vanilla',
      'vanilla-digital',
      // 'other'
    ] as InstrumentType[]
  ).includes(instrument_type)

  const [schedule, setSchedule] = useState<ScheduleLeg[] | null>(null)
  const [hasManualChanges, setHasManualChanges] = useState(false)

  const createInstrumentMutation = useCreateInstrumentMutation()

  const selectedInstrumentTypeLabel = INSTRUMENT_TYPE_OPTIONS.find(
    (i) => i.value === instrument_type,
  )?.label

  const resetCurrent = () => {
    reset({
      // @ts-expect-error
      instrument_type,
      ...defaultValues[instrument_type],
    })
    setSchedule(null)
    setHasManualChanges(false)
  }

  useEffect(() => {
    if (!instrument_type) return
    resetCurrent()
  }, [instrument_type])

  const removeEmptyFields = (data: AllInstrumentsSchemaType) => {
    const _data = data
    Object.keys(_data).forEach((key) => {
      const _key = key as keyof AllInstrumentsSchemaType
      // @ts-ignore
      if (_data[_key] === '' || _data[_key] == null || _data[_key] == undefined) {
        // @ts-ignore
        delete _data[key]
      }
    })
    return _data
  }

  const onSubmit = form.handleSubmit(async (data) => {
    return createInstrumentMutation.mutateAsync({
      ...removeEmptyFields(data),
      // @ts-ignore
      schedule: schedule?.map((leg) => ({
        ...leg,
        pay_date: leg.pay_date ? toISO8601(leg.pay_date) : leg.pay_date,
        start_observation_date: leg.start_observation_date
          ? toISO8601(leg.start_observation_date)
          : leg.start_observation_date,
        end_observation_date: leg.end_observation_date
          ? toISO8601(leg.end_observation_date)
          : leg.end_observation_date,
      })),
    })
  })

  return (
    <Page metadata={{ title: 'New Instrument' }} permissions={INSTRUMENTS.NEW.permissions}>
      <Dialog />
      <Form {...form}>
        <form onSubmit={onSubmit} className={'flex flex-col flex-1'}>
          <HeaderConfigBar sticky>
            <div className="flex items-center gap-10">
              <span className={'text-gray-700 font-semibold'}>Create a new Instrument</span>
              <div className="flex items-center gap-2">
                <FormField
                  name="instrument_type"
                  // @ts-ignore
                  control={form.control}
                  render={({ field: { value } }) => {
                    return (
                      <SearchSelect
                        size={'sm'}
                        placeholder={'Pick an instrument type...'}
                        value={value}
                        setValue={(val) =>
                          form.setValue('instrument_type', val as 'bond', { shouldValidate: true })
                        }
                        options={INSTRUMENT_TYPE_OPTIONS}
                      />
                    )
                  }}
                />
              </div>
            </div>
          </HeaderConfigBar>

          <ViewContainer className={cn('relative flex flex-col h-full gap-4')}>
            {instrument_type && (
              <DelayRender key={instrument_type}>
                <div
                  className={'flex flex-col gap-4 w-full h-fit p-4 rounded-md bg-gray-50/50 border'}
                >
                  <FormValues />
                  <div className="flex items-center gap-4 border-b-2 pb-3">
                    <span className={'font-bold text-gray-700 text-lg'}>
                      New {selectedInstrumentTypeLabel}{' '}
                      {!selectedInstrumentTypeLabel && 'instrument'}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={'link'}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => resetCurrent()}
                      >
                        <RefreshCcw />
                        Reset
                      </Button>
                      <Button size="sm" variant={'link'} disabled={!instrument_type} type="button">
                        <RefreshCcw />
                        Refresh Dropdowns
                      </Button>
                      {hasSchedule && (
                        <Button
                          size="sm"
                          variant={'outline'}
                          disabled={!isValid}
                          type="button"
                          onClick={async () => {
                            if (hasManualChanges) {
                              await withConfirmation(
                                () =>
                                  new Promise((res) => {
                                    setSchedule(getScheduleLegsFromFormValues(form.getValues()))
                                    res(true)
                                    setHasManualChanges(false)
                                  }),
                              )
                            } else {
                              setSchedule(getScheduleLegsFromFormValues(form.getValues()))
                              setHasManualChanges(false)
                            }
                          }}
                        >
                          <RefreshCcw />
                          Generate Schedule {hasManualChanges && '*'}
                        </Button>
                      )}
                      <FormField
                        name="database"
                        control={form.control}
                        render={({ field: { value } }) => {
                          return (
                            <SelectInput
                              size={'sm'}
                              value={value}
                              onValueChange={(val) =>
                                form.setValue('database', val as Database, { shouldValidate: true })
                              }
                              options={DATABASES.map((db) => ({
                                label: db.toUpperCase(),
                                value: db,
                              }))}
                            />
                          )
                        }}
                      />
                      <SubmitButton
                        size="sm"
                        variant="default"
                        disabled={!isValid || isSubmitting || (hasSchedule && !schedule)}
                        loading={isSubmitting}
                        icon={<Send />}
                      >
                        Submit
                      </SubmitButton>
                    </div>
                  </div>

                  <div className="relative @container flex-1">
                    {instrument_type === 'fxfwd' && <FxFwdFields />}
                    {instrument_type === 'bond' && <BondFormFields />}
                    {instrument_type === 'equity' && <EquityFormFields />}
                    {instrument_type === 'index' && <IndexFormFields />}
                    {instrument_type === 'aswap' && <AswapFormFields />}
                    {instrument_type === 'fund' && <FundFormFields />}
                    {instrument_type === 'diver-otc' && <DiverOtcFormFields />}
                    {instrument_type === 'equity-otc' && <EquityOtcFormFields />}
                    {instrument_type === 'autocall-standard' && <AutocallStandardFormFields />}
                    {instrument_type === 'autocall-consolation' && (
                      <AutocallConsolationFormFields />
                    )}
                    {instrument_type === 'autocall-phoenix' && <PhoenixNonMemoryFormFields />}
                    {instrument_type === 'digital' && <DigitalFormFields />}
                    {instrument_type === 'vanilla-spread' && <VanillaSpreadFormFields />}
                    {instrument_type === 'vanilla-structured' && <VanillaStructuredFormFields />}
                    {instrument_type === 'range-accrual' && <RangeAccrualFormFields />}
                    {instrument_type === 'supertracker' && <SupertrackerFormFields />}
                    {instrument_type === 'vanilla' && <VanillaFormFields />}
                    {instrument_type === 'vanilla-digital' && <VanillaDigitalFormFields />}
                    {instrument_type === 'other' && <OtherFormFields />}
                  </div>
                </div>
              </DelayRender>
            )}

            {!instrument_type && (
              <div className="flex flex-1 h-full bg-gray-100 rounded-md p-4">
                <div
                  className={
                    'max-w-75 flex-flex-col m-auto flex flex-col items-center fade-in animate-in duration-500'
                  }
                >
                  <>
                    {!instrument_type && (
                      <img
                        src={CreateInstrumentIllustration}
                        className={'object-cover w-full h-full opacity-80'}
                      />
                    )}
                    {!instrument_type && (
                      <span className="text-muted-foreground">
                        Select an instrument type to get started
                      </span>
                    )}
                  </>
                </div>
              </div>
            )}

            {hasSchedule && schedule !== null && (
              <ScheduleTable
                instrument_type={instrument_type}
                schedule={schedule}
                onChangeData={(data) => {
                  setSchedule(data)
                  setHasManualChanges(true)
                }}
              />
            )}
          </ViewContainer>
        </form>
      </Form>
    </Page>
  )
}
